import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  CircularProgress,
  Container,
  Stack,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import BoltRoundedIcon from '@mui/icons-material/BoltRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import LoginRoundedIcon from '@mui/icons-material/LoginRounded';
import RocketLaunchRoundedIcon from '@mui/icons-material/RocketLaunchRounded';
import SpaceDashboardRoundedIcon from '@mui/icons-material/SpaceDashboardRounded';
import { Suspense, lazy } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Route, Routes } from 'react-router-dom';
import { useAuth } from './features/auth/AuthContext';
// Eager-loaded (auth flow critical path):
import { LoginPage } from './features/auth/LoginPage';
import { ProtectedRoute } from './features/auth/ProtectedRoute';
import { AppLayout } from './layout/AppLayout';
import { AdminLayout } from './layout/AdminLayout';
import { IconBadge } from './layout/IconBadge';

// Lazy-loaded: każdy segment feature w osobnym chunk — redukuje initial bundle,
// ładuje się przy pierwszej nawigacji do strony.
const ForgotPasswordPage = lazy(() =>
  import('./features/auth/ForgotPasswordPage').then((m) => ({ default: m.ForgotPasswordPage })));
const ResetPasswordPage = lazy(() =>
  import('./features/auth/ResetPasswordPage').then((m) => ({ default: m.ResetPasswordPage })));
const AccountActivationPage = lazy(() =>
  import('./features/auth/AccountActivationPage').then((m) => ({ default: m.AccountActivationPage })));
const TwoFactorSetupPage = lazy(() =>
  import('./features/auth/TwoFactorSetupPage').then((m) => ({ default: m.TwoFactorSetupPage })));
const ProfilePage = lazy(() =>
  import('./features/auth/ProfilePage').then((m) => ({ default: m.ProfilePage })));
const DevTotpPage = lazy(() =>
  import('./features/dev/DevTotpPage').then((m) => ({ default: m.DevTotpPage })));

const PatientDetailPage = lazy(() =>
  import('./features/patients/PatientDetailPage').then((m) => ({ default: m.PatientDetailPage })));
const PatientListPage = lazy(() =>
  import('./features/patients/PatientListPage').then((m) => ({ default: m.PatientListPage })));

const ProjectDetailPage = lazy(() =>
  import('./features/projects/ProjectDetailPage').then((m) => ({ default: m.ProjectDetailPage })));
const ProjectFormDialog = lazy(() =>
  import('./features/projects/ProjectFormDialog').then((m) => ({ default: m.ProjectFormDialog })));
const ProjectListPage = lazy(() =>
  import('./features/projects/ProjectListPage').then((m) => ({ default: m.ProjectListPage })));

const DashboardPage = lazy(() =>
  import('./features/monitoring/DashboardPage').then((m) => ({ default: m.DashboardPage })));
const AlertsPage = lazy(() =>
  import('./features/monitoring/AlertsPage').then((m) => ({ default: m.AlertsPage })));
const ReportsPage = lazy(() =>
  import('./features/monitoring/ReportsPage').then((m) => ({ default: m.ReportsPage })));

const NotificationPreferencesPage = lazy(() =>
  import('./features/notifications/NotificationPreferencesPage').then((m) => ({ default: m.NotificationPreferencesPage })));

// Admin bundle — ładowany tylko dla ADMIN role (RBAC gate przy trasie).
const AdminDashboardPage = lazy(() =>
  import('./features/admin/AdminDashboardPage').then((m) => ({ default: m.AdminDashboardPage })));
const AuditLogPanel = lazy(() =>
  import('./features/admin/AuditLogPanel').then((m) => ({ default: m.AuditLogPanel })));
const BackupsPanel = lazy(() =>
  import('./features/admin/BackupsPanel').then((m) => ({ default: m.BackupsPanel })));
const DictionariesPanel = lazy(() =>
  import('./features/admin/DictionariesPanel').then((m) => ({ default: m.DictionariesPanel })));
const SystemConfigPanel = lazy(() =>
  import('./features/admin/SystemConfigPanel').then((m) => ({ default: m.SystemConfigPanel })));
const AdminUsersPanel = lazy(() =>
  import('./features/admin/users/AdminUsersPanel').then((m) => ({ default: m.AdminUsersPanel })));
const GdprPanel = lazy(() =>
  import('./features/gdpr/GdprPanel').then((m) => ({ default: m.GdprPanel })));

function RouteFallback(): JSX.Element {
  const { t } = useTranslation();
  return (
    <Container maxWidth="sm" sx={{ mt: 8, textAlign: 'center' }}>
      <CircularProgress aria-label={t('common.loading')} />
    </Container>
  );
}

function Home(): JSX.Element {
  const { t } = useTranslation();
  const { user } = useAuth();
  const theme = useTheme();

  const quickActions = [
    {
      to: '/dashboard',
      icon: <SpaceDashboardRoundedIcon />,
      color: 'info' as const,
      title: t('nav.dashboard'),
      desc: t('homeHub.shortcutDashboardDesc'),
    },
    {
      to: '/projects',
      icon: <BoltRoundedIcon />,
      color: 'primary' as const,
      title: t('nav.projects'),
      desc: t('homeHub.shortcutProjectsDesc'),
    },
    {
      to: '/patients',
      icon: <GroupsRoundedIcon />,
      color: 'success' as const,
      title: t('nav.patients'),
      desc: t('homeHub.shortcutPatientsDesc'),
    },
  ];

  return (
    <Container maxWidth="lg" disableGutters sx={{ py: 1 }}>
      <Stack spacing={3}>
        {/* Hero — gradient surface */}
        <Box
          sx={{
            position: 'relative',
            overflow: 'hidden',
            borderRadius: 3,
            border: `1px solid ${theme.palette.divider}`,
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.10)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 60%, transparent 100%)`,
            px: { xs: 2.5, md: 4.5 },
            py: { xs: 3, md: 4 },
          }}
        >
          <Box
            aria-hidden
            sx={{
              position: 'absolute',
              top: -80,
              right: -60,
              width: 320,
              height: 320,
              borderRadius: '50%',
              background: `radial-gradient(closest-side, ${alpha(theme.palette.primary.main, 0.30)}, transparent 70%)`,
              filter: 'blur(14px)',
              pointerEvents: 'none',
            }}
          />
          <Box
            aria-hidden
            sx={{
              position: 'absolute',
              bottom: -50,
              left: -30,
              width: 220,
              height: 220,
              borderRadius: '50%',
              background: `radial-gradient(closest-side, ${alpha(theme.palette.secondary.main, 0.20)}, transparent 70%)`,
              filter: 'blur(18px)',
              pointerEvents: 'none',
            }}
          />
          <Stack spacing={1.5} sx={{ position: 'relative', maxWidth: 720 }}>
            <Typography
              variant="overline"
              sx={{ color: 'primary.main', display: 'inline-flex', alignItems: 'center', gap: 0.75 }}
            >
              <RocketLaunchRoundedIcon sx={{ fontSize: 14 }} />
              {t('homeHub.header')}
            </Typography>
            <Typography variant="h2" component="h1" sx={{ letterSpacing: '-0.02em', fontWeight: 700 }}>
              {user ? t('homeHub.greeting', { name: user.firstName }) : t('app.title')}
            </Typography>
            <Typography
              variant="subtitle1"
              component="p"
              sx={{ color: 'text.secondary', maxWidth: 640 }}
            >
              {t('app.subtitle')}
            </Typography>
            <Stack direction="row" spacing={1.5} sx={{ pt: 1 }}>
              {user ? (
                <Button
                  component={Link}
                  to="/dashboard"
                  variant="contained"
                  size="large"
                  endIcon={<ArrowForwardRoundedIcon />}
                >
                  {t('homeHub.ctaDashboard')}
                </Button>
              ) : (
                <Button
                  component={Link}
                  to="/login"
                  variant="contained"
                  size="large"
                  startIcon={<LoginRoundedIcon />}
                >
                  {t('home.ctaLogin')}
                </Button>
              )}
            </Stack>
          </Stack>
        </Box>

        {/* Quick actions */}
        {user && (
          <Stack spacing={1.25}>
            <Typography variant="overline" sx={{ color: 'text.secondary' }}>
              Szybkie akcje
            </Typography>
            <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
              {quickActions.map((qa) => (
                <Card
                  key={qa.to}
                  sx={{
                    flex: '1 1 260px',
                    minWidth: 260,
                    transition: 'border-color 160ms ease, box-shadow 160ms ease',
                    ':hover': {
                      borderColor: 'primary.main',
                      boxShadow: `0 0 0 1px ${alpha(theme.palette.primary.main, 0.15)}`,
                    },
                  }}
                >
                  <CardActionArea component={Link} to={qa.to} sx={{ height: '100%' }}>
                    <CardContent sx={{ p: 2.5 }}>
                      <Stack spacing={2}>
                        <Stack direction="row" spacing={1.5} alignItems="center">
                          <IconBadge icon={qa.icon} color={qa.color} size="md" />
                          <ArrowForwardRoundedIcon
                            sx={{ ml: 'auto', color: 'text.secondary', fontSize: 20 }}
                          />
                        </Stack>
                        <Box>
                          <Typography variant="h6" sx={{ fontWeight: 700 }}>
                            {qa.title}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {qa.desc}
                          </Typography>
                        </Box>
                      </Stack>
                    </CardContent>
                  </CardActionArea>
                </Card>
              ))}
            </Stack>
          </Stack>
        )}

        <Alert severity="info" variant="outlined" sx={{ borderRadius: 2 }}>
          {t('home.phaseNotice')}
        </Alert>
      </Stack>
    </Container>
  );
}

function NotFound(): JSX.Element {
  const { t } = useTranslation();
  return (
    <Container maxWidth="sm" sx={{ py: 6 }}>
      <Stack spacing={2}>
        <Typography variant="h4" component="h1">
          {t('error.notFoundTitle')}
        </Typography>
        <Typography variant="body1">{t('error.notFoundMessage')}</Typography>
      </Stack>
    </Container>
  );
}

export default function App(): JSX.Element {
  return (
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route element={<AppLayout />}>
          <Route path="/" element={<Home />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/activate" element={<AccountActivationPage />} />
          <Route path="/dev/totp" element={<DevTotpPage />} />

          <Route element={<ProtectedRoute />}>
            <Route path="/2fa/setup" element={<TwoFactorSetupPage />} />
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/projects" element={<ProjectListPage />} />
            <Route path="/projects/new" element={<ProjectFormDialog />} />
            <Route path="/projects/:id" element={<ProjectDetailPage />} />
            <Route path="/projects/:id/edit" element={<ProjectFormDialog />} />
            <Route path="/patients" element={<PatientListPage />} />
            <Route path="/patients/:id" element={<PatientDetailPage />} />
            <Route path="/notifications/preferences" element={<NotificationPreferencesPage />} />
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/projects/:id/alerts" element={<AlertsPage />} />
            <Route path="/reports" element={<ReportsPage />} />

            <Route element={<AdminLayout />}>
              <Route element={<ProtectedRoute roles={['ADMIN']} />}>
                <Route path="/admin" element={<AdminDashboardPage />} />
                <Route path="/admin/users" element={<AdminUsersPanel />} />
                <Route path="/admin/config" element={<SystemConfigPanel />} />
                <Route path="/admin/dictionaries" element={<DictionariesPanel />} />
                <Route path="/admin/backups" element={<BackupsPanel />} />
                <Route path="/admin/audit-log" element={<AuditLogPanel />} />
              </Route>
              <Route element={<ProtectedRoute roles={['ADMIN', 'COORDINATOR']} />}>
                <Route path="/admin/gdpr" element={<GdprPanel />} />
              </Route>
            </Route>
          </Route>

          <Route path="*" element={<NotFound />} />
        </Route>
      </Routes>
    </Suspense>
  );
}
